// frontend/src/components/Auth/Register.jsx - VERSION CORRIGÉE

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth';
import { Form, Button, Container, Card, Alert, Row, Col, ProgressBar, InputGroup } from 'react-bootstrap';
import { useGoogleLogin } from '@react-oauth/google';
import { 
    FaUser, 
    FaEnvelope, 
    FaPhone, 
    FaLock, 
    FaGoogle,
    FaRocket, 
    FaCheckCircle, 
    FaShieldAlt, 
    FaEye, 
    FaEyeSlash 
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import './Auth.css';

const Register = () => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        password: '',
        confirm_password: '',
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { register } = useAuth();
    const navigate = useNavigate();

    const handleGoogleRegister = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            setLoading(true);
            setError('');
            try {
                const data = await authService.googleLogin(tokenResponse.access_token);
                const userData = data.user;
                toast.success('✅ Inscription Google réussie ! Bienvenue.');
                if (userData.role === 'superadmin') {
                    navigate('/superadmin');
                } else if (userData.role === 'admin') {
                    navigate('/admin');
                } else {
                    navigate('/dashboard');
                }
            } catch (err) {
                setError('Échec de l\'inscription avec Google. Veuillez réessayer.');
                toast.error('❌ Inscription Google échouée.');
            } finally {
                setLoading(false);
            }
        },
        onError: () => {
            setError('Inscription Google annulée ou échouée.');
            toast.error('❌ Inscription Google échouée.');
        },
    });

    const userAvatars = [
        'https://randomuser.me/api/portraits/women/44.jpg',
        'https://randomuser.me/api/portraits/men/32.jpg',
        'https://randomuser.me/api/portraits/women/68.jpg',
        'https://randomuser.me/api/portraits/men/22.jpg',
    ];

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (password.match(/[a-z]+/)) strength += 25;
        if (password.match(/[A-Z]+/)) strength += 25;
        if (password.match(/[0-9]+/)) strength += 25;
        return strength;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        if (name === 'password') {
            setPasswordStrength(calculatePasswordStrength(value));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirm_password) {
            setError('Les mots de passe ne correspondent pas');
            toast.error('❌ Les mots de passe ne correspondent pas');
            return;
        }

        if (!acceptTerms) {
            setError('Vous devez accepter les conditions d\'utilisation');
            toast.warning('⚠️ Acceptez les conditions pour continuer');
            return;
        }

        if (passwordStrength < 75) {
            setError('Le mot de passe doit être plus fort');
            toast.warning('⚠️ Mot de passe trop faible');
            return;
        }

        setLoading(true);

        try {
            const response = await register(formData);
            
            console.log('✅ Inscription réussie:', response);
            
            setSuccess(true);
            toast.success('✅ Compte créé ! Vérifiez votre email.');
            
        } catch (err) {
            console.error('❌ Erreur inscription:', err);
            
            // Gestion des différentes erreurs
            let errorMessage = 'Erreur lors de l\'inscription. Veuillez réessayer.';
            
            if (err.response) {
                const responseData = err.response.data;
                
                // Erreur serveur 500
                if (err.response.status === 500) {
                    errorMessage = responseData.error || 'Erreur serveur. Veuillez réessayer plus tard.';
                }
                // Erreur validation 422
                else if (err.response.status === 422) {
                    if (responseData.errors) {
                        if (responseData.errors.username) {
                            errorMessage = `Nom d'utilisateur: ${responseData.errors.username[0]}`;
                        } else if (responseData.errors.email) {
                            errorMessage = `Email: ${responseData.errors.email[0]}`;
                        } else if (responseData.errors.first_name) {
                            errorMessage = `Prénom: ${responseData.errors.first_name[0]}`;
                        } else if (responseData.errors.last_name) {
                            errorMessage = `Nom: ${responseData.errors.last_name[0]}`;
                        } else if (responseData.errors.password) {
                            errorMessage = `Mot de passe: ${responseData.errors.password[0]}`;
                        } else {
                            errorMessage = 'Erreurs de validation. Vérifiez vos données.';
                        }
                    } else if (responseData.message) {
                        errorMessage = responseData.message;
                    }
                }
                // Autres erreurs
                else if (responseData.message) {
                    errorMessage = responseData.message;
                } else if (responseData.error) {
                    errorMessage = responseData.error;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
            toast.error('❌ ' + errorMessage);
            
        } finally {
            setLoading(false);
        }
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength < 50) return 'danger';
        if (passwordStrength < 75) return 'warning';
        return 'success';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength < 50) return 'Faible';
        if (passwordStrength < 75) return 'Moyen';
        return 'Fort';
    };

    return (
        <div className="auth-page">
            <Container>
                <Row className="justify-content-center py-5">
                    <Col lg={10} xl={9}>
                        <Card className="auth-card">
                            <Row className="g-0">
                                {/* Left Side - Info */}
                                <Col md={5} className="auth-info-section">
                                    <div className="auth-info-content">
                                        <div className="auth-logo">
                                            <h1>🍁 E-Visa Canada</h1>
                                        </div>
                                        <h2 className="auth-info-title">
                                            Commencez votre aventure
                                        </h2>
                                        <p className="auth-info-text">
                                            Créez votre compte en 2 minutes et accédez 
                                            à tous nos services d'immigration.
                                        </p>
                                        
                                        <div className="registration-benefits-list">
                                            <div className="benefit-item">
                                                <FaRocket className="benefit-icon" />
                                                <div>
                                                    <h5>Processus rapide</h5>
                                                    <p>Dossier en quelques clics</p>
                                                </div>
                                            </div>
                                            <div className="benefit-item">
                                                <FaCheckCircle className="benefit-icon" />
                                                <div>
                                                    <h5>Suivi personnalisé</h5>
                                                    <p>Conseiller dédié</p>
                                                </div>
                                            </div>
                                            <div className="benefit-item">
                                                <FaShieldAlt className="benefit-icon" />
                                                <div>
                                                    <h5>100% sécurisé</h5>
                                                    <p>Données protégées</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="registration-stats-box">
                                            <p><strong>15,243</strong> personnes inscrites ce mois</p>
                                            <div className="user-avatars">
                                                {userAvatars.map((avatar, index) => (
                                                    <img 
                                                        key={index}
                                                        src={avatar} 
                                                        alt={`User ${index + 1}`}
                                                        className="avatar-circle"
                                                    />
                                                ))}
                                                <span className="more-count">+15K</span>
                                            </div>
                                        </div>
                                    </div>
                                </Col>

                                {/* Right Side - Form */}
                                <Col md={7} className="auth-form-section">
                                    <div className="auth-form-content">
                                        <h3 className="auth-form-title">Créer un compte</h3>
                                        <p className="auth-form-subtitle">
                                            Remplissez vos informations
                                        </p>

                                        {!success && (
                                            <>
                                                <div className="social-buttons mb-3">
                                                    <Button className="btn-social-google w-100" onClick={() => handleGoogleRegister()} disabled={loading}>
                                                        <FaGoogle className="me-2" />
                                                        S'inscrire avec Google
                                                    </Button>
                                                </div>
                                                <div className="auth-divider">
                                                    <span>OU</span>
                                                </div>
                                            </>
                                        )}

                                        {error && (
                                            <Alert variant="danger" className="auth-alert">
                                                <strong>⚠️ Erreur:</strong> {error}
                                            </Alert>
                                        )}

                                        {success ? (
                                            <Alert variant="success" className="mb-4">
                                                <div className="d-flex align-items-center mb-3">
                                                    <FaCheckCircle size={30} className="text-success me-3" />
                                                    <h5 className="mb-0">✅ Inscription réussie !</h5>
                                                </div>
                                                <p className="mb-2">
                                                    Un email de vérification a été envoyé à <strong>{formData.email}</strong>
                                                </p>
                                                <p className="mb-3">
                                                    📧 Veuillez cliquer sur le lien dans l'email pour activer votre compte.
                                                </p>
                                                <Alert variant="warning" className="mt-3">
                                                    ⚠️ <strong>Important:</strong> Si vous ne recevez pas l'email:
                                                    <ul className="mb-0 mt-2">
                                                        <li>Vérifiez votre dossier spam/courrier indésirable</li>
                                                        <li>Attendez quelques minutes (délai de livraison)</li>
                                                        <li>Utilisez le bouton ci-dessous pour renvoyer l'email</li>
                                                    </ul>
                                                </Alert>
                                                <hr />
                                                <div className="d-flex gap-2">
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={() => navigate('/login')}
                                                    >
                                                        Aller à la connexion
                                                    </Button>
                                                    <Button 
                                                        variant="outline-secondary"
                                                        onClick={() => navigate('/resend-verification')}
                                                    >
                                                        Renvoyer l'email
                                                    </Button>
                                                </div>
                                            </Alert>
                                        ) : (
                                            <Form onSubmit={handleSubmit} className="auth-form">
                                                <Row>
                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="form-label-custom">
                                                                Nom *
                                                            </Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text className="input-icon-group">
                                                                    <FaUser />
                                                                </InputGroup.Text>
                                                                <Form.Control
                                                                    type="text"
                                                                    name="last_name"
                                                                    value={formData.last_name}
                                                                    onChange={handleChange}
                                                                    required
                                                                    className="form-control-modern"
                                                                    placeholder="Ex: Dupont"
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="form-label-custom">
                                                                Prénoms *
                                                            </Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text className="input-icon-group">
                                                                    <FaUser />
                                                                </InputGroup.Text>
                                                                <Form.Control
                                                                    type="text"
                                                                    name="first_name"
                                                                    value={formData.first_name}
                                                                    onChange={handleChange}
                                                                    required
                                                                    className="form-control-modern"
                                                                    placeholder="Ex: Jean"
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>

                                                <Form.Group className="mb-3">
                                                    <Form.Label className="form-label-custom">
                                                        Nom d'utilisateur *
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="input-icon-group">
                                                            <FaUser />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="username"
                                                            value={formData.username}
                                                            onChange={handleChange}
                                                            required
                                                            className="form-control-modern"
                                                            placeholder="Identifiant unique"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>

                                                <Form.Group className="mb-3">
                                                    <Form.Label className="form-label-custom">
                                                        Email *
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="input-icon-group">
                                                            <FaEnvelope />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            name="email"
                                                            value={formData.email}
                                                            onChange={handleChange}
                                                            required
                                                            className="form-control-modern"
                                                            placeholder="votre@email.com"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>

                                                <Form.Group className="mb-3">
                                                    <Form.Label className="form-label-custom">
                                                        Téléphone *
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="input-icon-group">
                                                            <FaPhone />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="tel"
                                                            name="phone"
                                                            value={formData.phone}
                                                            onChange={handleChange}
                                                            required
                                                            className="form-control-modern"
                                                            placeholder="+1 XXX XXX XXXX"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>

                                                <Form.Group className="mb-3">
                                                    <Form.Label className="form-label-custom">
                                                        Mot de passe *
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="input-icon-group">
                                                            <FaLock />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type={showPassword ? "text" : "password"}
                                                            name="password"
                                                            value={formData.password}
                                                            onChange={handleChange}
                                                            required
                                                            className="form-control-modern"
                                                            placeholder="Min 8 caractères"
                                                        />
                                                        <InputGroup.Text 
                                                            className="input-icon-group password-toggle"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                        </InputGroup.Text>
                                                    </InputGroup>
                                                    {formData.password && (
                                                        <div className="password-strength mt-2">
                                                            <ProgressBar 
                                                                now={passwordStrength} 
                                                                variant={getPasswordStrengthColor()}
                                                                style={{height: '6px'}}
                                                            />
                                                            <small className={`text-${getPasswordStrengthColor()}`}>
                                                                Force: {getPasswordStrengthText()}
                                                            </small>
                                                        </div>
                                                    )}
                                                </Form.Group>

                                                <Form.Group className="mb-4">
                                                    <Form.Label className="form-label-custom">
                                                        Confirmer *
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="input-icon-group">
                                                            <FaLock />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type={showConfirmPassword ? "text" : "password"}
                                                            name="confirm_password"
                                                            value={formData.confirm_password}
                                                            onChange={handleChange}
                                                            required
                                                            className="form-control-modern"
                                                            placeholder="Retapez votre mot de passe"
                                                        />
                                                        <InputGroup.Text 
                                                            className="input-icon-group password-toggle"
                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                                        </InputGroup.Text>
                                                    </InputGroup>
                                                </Form.Group>

                                                <Form.Group className="mb-4">
                                                    <Form.Check 
                                                        type="checkbox"
                                                        checked={acceptTerms}
                                                        onChange={(e) => setAcceptTerms(e.target.checked)}
                                                        label={
                                                            <span>
                                                                J'accepte les{' '}
                                                                <Link to="/terms" className="auth-link-primary">
                                                                    conditions
                                                                </Link>
                                                                {' '}et la{' '}
                                                                <Link to="/privacy" className="auth-link-primary">
                                                                    politique
                                                                </Link>
                                                            </span>
                                                        }
                                                        className="custom-checkbox"
                                                    />
                                                </Form.Group>

                                                <Button
                                                    type="submit"
                                                    className="btn-submit-modern w-100"
                                                    disabled={loading || !acceptTerms}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                                            Création...
                                                        </>
                                                    ) : (
                                                        <>Créer mon compte →</>
                                                    )}
                                                </Button>
                                            </Form>
                                        )}



                                        <div className="auth-footer-text">
                                            <p>
                                                Déjà un compte ?{' '}
                                                <Link to="/login" className="auth-link-primary">
                                                    Connectez-vous
                                                </Link>
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Register;